import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Button, Card, Col, Empty, Grid, Row, Space, Spin, Tag, Typography } from 'antd';
import { ArrowLeftOutlined, BookOutlined } from '@ant-design/icons';

import {
  getCycle,
  getCycleClasses,
  getCycleMatieres,
  getCycleSousMatieres,
  ICycleClasse,
  ICycleMatiere,
  ICycleSousMatiere,
} from './cycle.reducer';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { scoringModeLabels } from 'app/shared/model/cycle.model';

const { Text, Title } = Typography;
const { useBreakpoint } = Grid;

export const CycleMatieresView = (props: RouteComponentProps<{ ecoleid: string; anneeScolaireId?: string; id: string }>) => {
  const dispatch = useAppDispatch();
  const screens = useBreakpoint();
  const cycleEntity = useAppSelector(state => state.cycle.entity);
  const [classes, setClasses] = useState<ICycleClasse[]>([]);
  const [matieres, setMatieres] = useState<ICycleMatiere[]>([]);
  const [sousMatieresByCycleMatiereId, setSousMatieresByCycleMatiereId] = useState<Record<string, ICycleSousMatiere[]>>({});
  const [loading, setLoading] = useState(false);

  const cycleId = props.match.params.id;

  const loadCycleMatieres = useCallback(async () => {
    if (!cycleId) return;

    setLoading(true);
    try {
      const rows = await dispatch(getCycleMatieres(cycleId)).unwrap();
      const cycleClasses = await dispatch(getCycleClasses(cycleId)).unwrap();
      setMatieres(rows);
      setClasses(cycleClasses);

      const rowsWithSousMatieres = rows.filter(matiere => matiere.avecSousMatieres && matiere.cycleMatiereId);
      const entries = await Promise.all(
        rowsWithSousMatieres.map(async matiere => {
          const cycleMatiereId = matiere.cycleMatiereId;
          const response = await dispatch(getCycleSousMatieres({ cycleMatiereId, cycleId, matiereId: matiere.id })).unwrap();
          return [cycleMatiereId, response] as [string, ICycleSousMatiere[]];
        })
      );

      setSousMatieresByCycleMatiereId(
        entries.reduce<Record<string, ICycleSousMatiere[]>>((acc, [cycleMatiereId, items]) => {
          acc[cycleMatiereId] = items;
          return acc;
        }, {})
      );
    } finally {
      setLoading(false);
    }
  }, [cycleId, dispatch]);

  useEffect(() => {
    dispatch(getCycle(cycleId));
    loadCycleMatieres();
  }, [cycleId, dispatch, loadCycleMatieres]);

  const totalSousMatieres = useMemo(
    () => Object.values(sousMatieresByCycleMatiereId).reduce((total, items) => total + items.length, 0),
    [sousMatieresByCycleMatiereId]
  );

  const getSousMatieresForMatiere = (matiere: ICycleMatiere) =>
    matiere.cycleMatiereId ? sousMatieresByCycleMatiereId[matiere.cycleMatiereId] || [] : [];

  return (
    <Card style={{ margin: screens.xs ? '16px 8px' : '24px' }} bodyStyle={{ padding: screens.xs ? 16 : 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: screens.xs ? 'flex-start' : 'center',
            flexDirection: screens.xs ? 'column' : 'row',
            gap: 12,
          }}
        >
          <Space direction="vertical" size={2}>
            <Text type="secondary">Matières et sous-matières du cycle</Text>
            <Title level={3} style={{ margin: 0 }}>
              {cycleEntity?.nom || '-'}
            </Title>
            <Text type="secondary">{cycleEntity?.scoringMode ? scoringModeLabels[cycleEntity.scoringMode] : '-'}</Text>
          </Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => props.history.goBack()}>
            Retour
          </Button>
        </div>

        <Row gutter={[12, 12]}>
          <Col xs={24} md={8}>
            <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 14, background: '#fafafa' }}>
              <Text type="secondary">Classes</Text>
              <Title level={4} style={{ margin: '2px 0 0' }}>
                {classes.length}
              </Title>
            </div>
          </Col>
          <Col xs={24} md={8}>
            <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 14, background: '#fafafa' }}>
              <Text type="secondary">Matières</Text>
              <Title level={4} style={{ margin: '2px 0 0' }}>
                {matieres.length}
              </Title>
            </div>
          </Col>
          <Col xs={24} md={8}>
            <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 14, background: '#fafafa' }}>
              <Text type="secondary">Sous-matières</Text>
              <Title level={4} style={{ margin: '2px 0 0' }}>
                {totalSousMatieres}
              </Title>
            </div>
          </Col>
        </Row>

        <Spin spinning={loading}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <section>
              <Title level={4} style={{ marginTop: 0 }}>
                Classes du cycle
              </Title>
              {classes.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucune classe liée à ce cycle" />
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {classes.map(classe => (
                    <Tag key={classe.id} color="blue" style={{ marginRight: 0, padding: '3px 10px' }}>
                      {classe.nom}
                    </Tag>
                  ))}
                </div>
              )}
            </section>

            <section>
              <Title level={4} style={{ marginTop: 0 }}>
                Matières du cycle
              </Title>
              {matieres.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucune matière configurée pour ce cycle" />
              ) : (
                <Space direction="vertical" size={10} style={{ width: '100%' }}>
                  {matieres.map(matiere => {
                    const sousMatieres = getSousMatieresForMatiere(matiere);
                    return (
                      <div
                        key={matiere.id}
                        style={{
                          border: '1px solid #f0f0f0',
                          borderRadius: 6,
                          padding: screens.xs ? 12 : 16,
                          background: '#fff',
                        }}
                      >
                        <Space direction="vertical" size={8} style={{ width: '100%' }}>
                          <Space size={8} wrap>
                            <BookOutlined style={{ color: '#f28e19' }} />
                            <Text strong>{matiere.nom}</Text>
                          </Space>

                          {matiere.avecSousMatieres &&
                            (sousMatieres.length > 0 ? (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                {sousMatieres.map(sousMatiere => (
                                  <Tag key={sousMatiere.id} style={{ marginRight: 0, padding: '2px 8px' }}>
                                    {sousMatiere.nom}
                                  </Tag>
                                ))}
                              </div>
                            ) : (
                              <Text type="secondary">Aucune sous-matière renseignée.</Text>
                            ))}
                        </Space>
                      </div>
                    );
                  })}
                </Space>
              )}
            </section>
          </Space>
        </Spin>
      </Space>
    </Card>
  );
};

export default CycleMatieresView;
